import React, { useState, useEffect } from "react"
import * as layouts from "layouts"
import * as icons from "icons"
import * as sessions from "sessions"
import * as api from "brandguard/api"
import * as pagecache from "brandguard/display/brandguard/documents/cache"
import styled from "@emotion/styled"

interface documentProps {
	item: api.DocumentSearchResponseItem
	onClick?(): void
	showNavigation?: boolean
	onPageChange?(pageIndex: number, page: api.DocumentPage | undefined): void
}

const PDFContainer = styled(layouts.containers.flex)`
	width: 100%;
	height: 100%;
	align-items: center;
	justify-content: center;
	overflow: auto;
`

export function Document(props: React.PropsWithChildren<documentProps> & layouts.containers.FlexProps): JSX.Element {
	const { item, onClick, showNavigation, onPageChange, children, ...rest } = props
	const [currentPageIndex, setCurrentPageIndex] = useState(0)
	const [pageUrl, setPageUrl] = useState("")
	const [loading, setLoading] = useState(false)
	const bearertoken = sessions.useToken()
	const account = sessions.useAccount()

	const currentPage = item.pages?.[currentPageIndex]
	const currentPageId = currentPage?.id

	// Fetch signed URL for current page
	useEffect(() => {
		if (!currentPageId) {
			setPageUrl("")
			setLoading(false)
			return
		}

		setLoading(true)
		const p = pagecache
			.current(account.id, currentPageId, bearertoken)
			.then((url) => {
				setPageUrl(url)
				setLoading(false)
			})
			.catch((cause) => {
				console.error("unable to load document page", cause)
				setPageUrl("")
				setLoading(false)
			})

		return () => {
			if (p && typeof p.cancel === "function") {
				p.cancel()
			}
		}
	}, [currentPageId])

	// Reset to first page when item changes
	useEffect(() => {
		setCurrentPageIndex(0)
		onPageChange?.(0, item.pages?.[0])
	}, [item.media?.id])

	const nextPage = () => {
		if (item.pages && currentPageIndex < item.pages.length - 1) {
			const newIndex = currentPageIndex + 1
			setCurrentPageIndex(newIndex)
			onPageChange?.(newIndex, item.pages[newIndex])
		}
	}

	const prevPage = () => {
		if (currentPageIndex > 0) {
			const newIndex = currentPageIndex - 1
			setCurrentPageIndex(newIndex)
			onPageChange?.(newIndex, item.pages?.[newIndex])
		}
	}

	const hasMultiplePages = item.pages && item.pages.length > 1

	const content =
		currentPage && pageUrl ? (
			<img
				src={pageUrl}
				alt={`Page ${currentPageIndex + 1}`}
				style={{
					maxWidth: "100%",
					maxHeight: "100%",
					objectFit: "contain",
					display: "block"
				}}
			/>
		) : (
			<layouts.containers.flex flexDirection="column" alignItems="center" justifyContent="center" width="100%" height="100%">
				<icons.brandguard.document width="80px" height="80px" />
				<layouts.containers.span fontSize="12px" color={layouts.theme.colors.grey.medium} mt="10px">
					{item.pages?.length || 0} page{(item.pages?.length || 0) !== 1 ? "s" : ""}
				</layouts.containers.span>
				{!currentPage && <layouts.Span fontSize="10px" mt="5px" color={layouts.theme.colors.grey.dark50}>Loading page...</layouts.Span>}
				{currentPage && !pageUrl && !loading && (
					<layouts.Span fontSize="10px" mt="5px" color={layouts.theme.colors.grey.dark50}>
						Page not available
					</layouts.Span>
				)}
			</layouts.containers.flex>
		)

	return (
		<layouts.containers.flex
			flexDirection="column"
			overflow="hidden"
			flex="1"
			onClick={onClick}
			position="relative"
			{...rest}
		>
			<layouts.loading.screen loading={loading} flex="1" icon={<></>} height="100%">
				<PDFContainer>{content}</PDFContainer>
			</layouts.loading.screen>

			{/* Page navigation arrows - only show if enabled and multiple pages exist */}
			{showNavigation && hasMultiplePages && (
				<>
					<layouts.containers.absolute top="10px" left="50%" style={{ transform: "translateX(-50%)" }}>
						<layouts.Flex
							background={`${layouts.theme.colors.white}BF`}
							boxShadow="0px 1px 2px 0px #00000040"
							alignItems="center"
							justifyContent="center"
							borderRadius="50%"
							width="32px"
							height="32px"
							cursor="pointer"
							onClick={(e) => {
								e.stopPropagation()
								prevPage()
							}}
							opacity={currentPageIndex === 0 ? 0.3 : 1}
						>
							<icons.arrows.ArrowUp />
						</layouts.Flex>
					</layouts.containers.absolute>
					<layouts.containers.absolute bottom="10px" left="50%" style={{ transform: "translateX(-50%)" }}>
						<layouts.Flex
							background={`${layouts.theme.colors.white}BF`}
							boxShadow="0px 1px 2px 0px #00000040"
							alignItems="center"
							justifyContent="center"
							borderRadius="50%"
							width="32px"
							height="32px"
							cursor="pointer"
							onClick={(e) => {
								e.stopPropagation()
								nextPage()
							}}
							opacity={currentPageIndex === item.pages!.length - 1 ? 0.3 : 1}
						>
							<icons.arrows.ArrowDown />
						</layouts.Flex>
					</layouts.containers.absolute>
					<layouts.containers.absolute bottom="10px" right="10px">
						<layouts.containers.span fontSize="10px" color={layouts.theme.colors.grey.dark50} background={`${layouts.theme.colors.white}E6`} padding="2px 6px" borderRadius="4px">
							Page {currentPageIndex + 1} of {item.pages?.length || 0}
						</layouts.containers.span>
					</layouts.containers.absolute>
				</>
			)}
			{children}
		</layouts.containers.flex>
	)
}

Document.defaultProps = {
	showNavigation: true,
}
